import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { X, Save, Search, Check } from 'lucide-react';
import * as Icons from 'lucide-react';

const RoleEditor = ({ role, permissions, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedModules, setExpandedModules] = useState({});

  // Initialize form with existing role data
  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions || []
      });
    }
  }, [role]);

  // Toggle module expansion
  const toggleModule = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  // Check if permission is selected
  const isPermissionSelected = (permissionKey) => {
    return formData.permissions.includes(permissionKey);
  };

  // Toggle permission selection
  const togglePermission = (permissionKey) => {
    setFormData(prev => {
      const isSelected = prev.permissions.includes(permissionKey);
      return {
        ...prev,
        permissions: isSelected
          ? prev.permissions.filter(p => p !== permissionKey)
          : [...prev.permissions, permissionKey]
      };
    });
  };

  // Select all permissions for a module
  const selectModulePermissions = (module) => {
    const modulePermissionKeys = module.permissions.map(p => p.key);
    const allSelected = modulePermissionKeys.every(key => formData.permissions.includes(key));
    
    setFormData(prev => {
      if (allSelected) {
        // Deselect all
        return {
          ...prev,
          permissions: prev.permissions.filter(p => !modulePermissionKeys.includes(p))
        };
      } else {
        // Select all
        const newPermissions = [...new Set([...prev.permissions, ...modulePermissionKeys])];
        return {
          ...prev,
          permissions: newPermissions
        };
      }
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Le nom du rôle est requis');
      return;
    }
    
    if (formData.permissions.length === 0) {
      alert('Sélectionnez au moins une permission');
      return;
    }
    
    onSave(formData);
  };

  // Filter modules based on search
  const filteredModules = permissions.modules?.filter(module => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return module.label.toLowerCase().includes(searchLower) ||
           module.key.toLowerCase().includes(searchLower) ||
           module.permissions?.some(p => 
             p.action.toLowerCase().includes(searchLower) ||
             p.description.toLowerCase().includes(searchLower)
           );
  });

  // Get icon component
  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <Icons.Box className="h-5 w-5" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">
                {role ? 'Modifier le Rôle' : 'Créer un Nouveau Rôle'}
              </CardTitle>
              <CardDescription>
                Configurez le nom, la description et les permissions du rôle
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={onCancel}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <CardContent className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Basic information */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nom du Rôle *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Responsable Logistique"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Décrivez les responsabilités de ce rôle..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="flex items-center gap-4 text-sm">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  {formData.permissions.length} permission{formData.permissions.length > 1 ? 's' : ''} sélectionnée{formData.permissions.length > 1 ? 's' : ''}
                </Badge>
              </div>
            </div>

            {/* Permissions selection */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Permissions</h3>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-3">
                {filteredModules?.map((module) => {
                  const isExpanded = expandedModules[module.key];
                  const modulePermissionKeys = module.permissions.map(p => p.key);
                  const selectedCount = modulePermissionKeys.filter(key => 
                    formData.permissions.includes(key)
                  ).length;
                  const allSelected = selectedCount === modulePermissionKeys.length;

                  return (
                    <div key={module.key} className="border rounded-lg overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleModule(module.key)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-emerald-600">
                            {getIcon(module.icon)}
                          </div>
                          <div>
                            <h4 className="font-medium">{module.label}</h4>
                            <p className="text-xs text-gray-600">
                              {selectedCount}/{modulePermissionKeys.length} sélectionnée{selectedCount > 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant={allSelected ? "default" : "outline"}
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectModulePermissions(module);
                            }}
                            className={allSelected ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                          >
                            {allSelected ? (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                Tout désélectionner
                              </>
                            ) : (
                              'Tout sélectionner'
                            )}
                          </Button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="p-4 space-y-2 bg-white">
                          {module.permissions?.map((permission) => {
                            const isSelected = isPermissionSelected(permission.key);
                            
                            return (
                              <label
                                key={permission.key}
                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                  isSelected
                                    ? 'bg-emerald-50 border-emerald-300'
                                    : 'bg-gray-50 border-gray-200 hover:bg-white'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => togglePermission(permission.key)}
                                  className="mt-1 h-4 w-4 text-emerald-600 rounded focus:ring-emerald-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline" className="text-xs">
                                      {permission.action}
                                    </Badge>
                                    <span className="text-sm font-medium">{permission.description}</span>
                                  </div>
                                  <p className="text-xs text-gray-500 font-mono">{permission.key}</p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>

          {/* Footer actions */}
          <div className="border-t p-4 flex items-center justify-end gap-3 bg-gray-50">
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="h-4 w-4 mr-2" />
              {role ? 'Enregistrer les modifications' : 'Créer le rôle'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default RoleEditor;
